import React, { useState, useEffect, useRef } from "react";
import {
	fetchUsers,
	updateUser,
	deleteUser,
	createUser,
	fetchDepartments,
	fetchEntitlements,
	updateUserEntitlement, // NEW
} from "./api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Users() {
	// New user (password required by backend)
	const [newUser, setNewUser] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		vacation_hours: 0, // read-only (back-compat)
		total_vacation_hours: 160,
		start_date: "2025-01-01",
		department_id: "",
	});

	const [users, setUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);

	const [searchTerm, setSearchTerm] = useState("");
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);

	const [departments, setDepartments] = useState([]);

	// Entitlements per user for selectedYear
	const [entitlementsMap, setEntitlementsMap] = useState(new Map());
	const [loadingEntitlements, setLoadingEntitlements] = useState(false);

	// Drag-to-scroll ref
	const tableContainerRef = useRef(null);

	const [entitlementEdits, setEntitlementEdits] = useState({});
	const onChangeEntitlement = (userId, value) => {
		setEntitlementEdits((prev) => ({ ...prev, [userId]: value }));
	};

	// Department row colors
	const departmentColors = {
		Production: "bg-green-100",
		Cleaning: "bg-gray-100",
		Directors: "bg-teal-100",
		Engineers: "bg-yellow-100",
		Floor: "bg-orange-100",
		G3: "bg-cyan-100",
		Office: "bg-red-100",
		Process: "bg-pink-100",
		QC: "bg-blue-100",
		Warehouse: "bg-purple-100",
	};

	// ---- Data loading ----
	const fetchAllDepartments = async () => {
		try {
			const { data } = await fetchDepartments();
			setDepartments(data);
		} catch {
			toast.error("Failed to fetch departments");
		}
	};

	const fetchAllUsers = async () => {
		try {
			const { data } = await fetchUsers();
			const updated = data.map((u) => ({
				...u,
				start_date: u.start_date || "2025-01-01",
				total_vacation_hours: u.total_vacation_hours || 160,
			}));
			setUsers(updated);
			setFilteredUsers(updated);
		} catch {
			toast.error("Failed to fetch users");
		}
	};

	const loadEntitlementsForYear = async (year) => {
		setLoadingEntitlements(true);
		try {
			const { data } = await fetchEntitlements(year);
			// [{ user_id, year, entitlement_hours, carried_over_hours, adjustments_hours, taken_hours, remaining_hours }]
			const map = new Map(data.map((e) => [e.user_id, e]));
			setEntitlementsMap(map);
		} catch {
			// Not blocking: show zeros if nothing returned
			setEntitlementsMap(new Map());
		} finally {
			setLoadingEntitlements(false);
		}
	};

	useEffect(() => {
		fetchAllDepartments();
	}, []);

	useEffect(() => {
		if (departments.length > 0) {
			fetchAllUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [departments]);

	useEffect(() => {
		loadEntitlementsForYear(selectedYear);
	}, [selectedYear]);

	// Filter by search term ONLY (do NOT filter by year)
	useEffect(() => {
		const hay = (s) =>
		(s || "").toLowerCase().includes(searchTerm.toLowerCase());
		const results = users.filter(
			(user) =>
			(user.firstName && hay(user.firstName)) ||
			(user.lastName && hay(user.lastName)) ||
			(user.email && hay(user.email))
		);
		setFilteredUsers(results);
	}, [searchTerm, users]);

	// ---- Handlers ----
	const handleInputChange = (index, field, value) => {
		const updated = [...filteredUsers];
		if (field === "department_id") {
			const selectedDept = departments.find((d) => d.id === parseInt(value));
			updated[index]["department_id"] = selectedDept ? parseInt(value) : "";
			updated[index]["department"] = selectedDept
				? selectedDept.name
			: "No Department";
		} else if (field === "total_vacation_hours") {
			updated[index][field] = value === "" ? "" : Number(value);
		} else {
			updated[index][field] = value;
		}
		setFilteredUsers(updated);
	};

	const handleSave = async (id, user) => {
		const payload = {
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			total_vacation_hours:
			user.total_vacation_hours === ""
			? 0
			: Number(user.total_vacation_hours),
			start_date: user.start_date,
			department_id: user.department_id || null,
		};

		try {
			await updateUser(id, payload);
			// push the yearly entitlement if edited
			const ent = entitlementsMap.get(id);
			const edited = entitlementEdits[id];
			const originalEnt = ent?.entitlement_hours;

			if (edited !== undefined && Number(edited) !== Number(originalEnt)) {
				try {
					await updateUserEntitlement(id, selectedYear, {
						entitlement_hours: Number(edited),
						// (optionnel) tu peux exposer ces champs plus tard dans l’UI :
						// carried_over_hours: ent?.carried_over_hours ?? 0,
						// adjustments_hours:  ent?.adjustments_hours ?? 0,
					});
				} catch (e) {
					toast.error(
						e?.response?.data?.message || "Failed to update yearly entitlement"
					);
					return; // on sort pour éviter le toast de succès
				}
			}

			// refresh maps
			await fetchAllUsers();
			await loadEntitlementsForYear(selectedYear);
			setEntitlementEdits((prev) => ({ ...prev, [id]: undefined }));
			toast.success(`User ${user.email} updated successfully`);
		} catch {
			toast.error("Failed to update user");
		}
	};

	const handleDelete = async (id) => {
		try {
			await deleteUser(id);
			setUsers((prev) => prev.filter((u) => u.id !== id));
			toast.success("User deleted successfully");
			setIsDeleteModalOpen(false);
		} catch {
			toast.error("Failed to delete user");
		}
	};

	const handleNewUserInputChange = (field, value) => {
		if (field === "vacation_hours") return; // read-only
		setNewUser((prev) => ({ ...prev, [field]: value }));
	};

	const handleCreateUser = async () => {
		if (
			!newUser.firstName ||
			!newUser.lastName ||
			!newUser.email ||
			!newUser.password
		) {
			toast.error("Please fill first name, last name, email and password");
			return;
		}
		const payload = {
			firstName: newUser.firstName,
			lastName: newUser.lastName,
			email: newUser.email,
			password: newUser.password,
			total_vacation_hours: Number(newUser.total_vacation_hours || 0),
			start_date: newUser.start_date,
			department_id: newUser.department_id || null,
		};
		try {
			await createUser(payload);
			await fetchAllUsers();
			await loadEntitlementsForYear(selectedYear);
			toast.success(`User ${newUser.email} created successfully`);
			setIsModalOpen(false);
			setNewUser({
				firstName: "",
				lastName: "",
				email: "",
				password: "",
				vacation_hours: 0,
				total_vacation_hours: 160,
				start_date: "2025-01-01",
				department_id: "",
			});
		} catch {
			toast.error("Failed to create user");
		}
	};

	// Export CSV (includes year columns)
	const exportCSV = () => {
		let csvContent = "data:text/csv;charset=utf-8,";
		const header = [
			"First Name",
			"Last Name",
			"Email",
			"Role",
			"Vacation Hours (current/back-compat)",
			`Year Entitlement (${selectedYear})`,
			`Year Carried Over (${selectedYear})`,
			`Year Adjustments (${selectedYear})`,
			`Year Taken (${selectedYear})`,
			`Year Remaining (${selectedYear})`,
			"Start Date",
			"Department",
		];
		csvContent += header.join(",") + "\r\n";

		filteredUsers.forEach((user) => {
			const ent = entitlementsMap.get(user.id) || {};
			const row = [
				user.firstName || "",
				user.lastName || "",
				user.email || "",
				user.role || "",
				user.vacation_hours ?? "",
				ent.entitlement_hours ?? 0,
				ent.carried_over_hours ?? 0,
				ent.adjustments_hours ?? 0,
				ent.taken_hours ?? 0,
				Math.max(ent.remaining_hours ?? 0, 0),
				user.start_date || "",
				user.department || "",
			];
			csvContent += row.map((f) => `"${f}"`).join(",") + "\r\n";
		});

		const encodedUri = encodeURI(csvContent);
		const link = document.createElement("a");
		link.setAttribute("href", encodedUri);
		link.setAttribute("download", `users_${selectedYear}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	// Drag-to-scroll
	useEffect(() => {
		const container = tableContainerRef.current;
		if (!container) return;

		let isDown = false;
		let startX;
		let scrollLeft;

		const mouseDownHandler = (e) => {
			isDown = true;
			container.classList.add("active");
			startX = e.pageX - container.offsetLeft;
			scrollLeft = container.scrollLeft;
		};

		const mouseLeaveHandler = () => {
			isDown = false;
			container.classList.remove("active");
		};

		const mouseUpHandler = () => {
			isDown = false;
			container.classList.remove("active");
		};

		const mouseMoveHandler = (e) => {
			if (!isDown) return;
			e.preventDefault();
			const x = e.pageX - container.offsetLeft;
			const walk = (x - startX) * 1;
			container.scrollLeft = scrollLeft - walk;
		};

		container.addEventListener("mousedown", mouseDownHandler);
		container.addEventListener("mouseleave", mouseLeaveHandler);
		container.addEventListener("mouseup", mouseUpHandler);
		container.addEventListener("mousemove", mouseMoveHandler);

		return () => {
			container.removeEventListener("mousedown", mouseDownHandler);
			container.removeEventListener("mouseleave", mouseLeaveHandler);
			container.removeEventListener("mouseup", mouseUpHandler);
			container.removeEventListener("mousemove", mouseMoveHandler);
		};
	}, []);

	return (
		<div className="container mx-auto p-6">
		<h1 className="text-3xl font-semibold mb-2 text-center">Manage Users</h1>
		<p className="text-center text-sm text-gray-600 mb-6">
		Columns “Year Entitlement” & “Year Remaining” reflect the selected year.{" "}
“Vacation Hours” is a current-year back-compat balance.
	</p>

<div className="flex justify-between items-center mb-6">
	<div className="flex space-x-4">
		<input
type="text"
placeholder="Name / Email"
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
className="w-64 px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
/>
	<button
onClick={exportCSV}
className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
>
	Export CSV
		</button>
</div>

<div className="flex items-center space-x-4">
	<label className="text-gray-700">Select Year:</label>
<input
type="number"
value={selectedYear}
onChange={(e) => setSelectedYear(parseInt(e.target.value || 0, 10))}
className="w-24 px-2 py-1 border border-gray-300 rounded"
/>
	{loadingEntitlements && (
	 <span className="text-sm text-gray-500">Loading…</span>
	 )}
		 </div>

<button
onClick={() => setIsModalOpen(true)}
className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
>
	Create User
		</button>
</div>

{/* Table (drag-to-scroll) */}
<div
ref={tableContainerRef}
className="w-full max-w-full overflow-x-auto"
>
	<table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-md table-auto">
		<thead className="bg-gold">
			<tr>
			<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
				First Name
					</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Last Name
		</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Email
</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Role
</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Vacation Hours (selected year)
</th>

<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Year Remaining
		</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Start Date
		</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Department
</th>
<th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
	Actions
</th>
</tr>
</thead>
<tbody className="divide-y divide-gray-200">
	{filteredUsers.map((user, index) => {
		const ent = entitlementsMap.get(user.id);
		const yearEntitlement = ent?.entitlement_hours ?? 0;
		const yearRemaining = Number(ent?.remaining_hours ?? 0);

		return (
			<tr
			key={user.id}
className={`hover:bg-gray-100 ${
		   departmentColors[user.department] || ""
}`}
                >
                  <td className="px-2 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={user.firstName || ""}
                      onChange={(e) =>
                        handleInputChange(index, "firstName", e.target.value)
                      }
                      className="text-sm font-medium text-gray-900 border border-gray-300 rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={user.lastName || ""}
                      onChange={(e) =>
                        handleInputChange(index, "lastName", e.target.value)
                      }
                      className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <input
                      type="email"
                      value={user.email || ""}
                      onChange={(e) =>
                        handleInputChange(index, "email", e.target.value)
                      }
                      className="text-sm text-gray-500 border border-gray-300 rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.role}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    {(() => {
                      const ent = entitlementsMap.get(user.id);
                      const currentEnt =
                        entitlementEdits[user.id] ??
                        ent?.entitlement_hours ??
                        0;
                      return (
                        <input
                          type="number"
                          value={currentEnt}
                          onChange={(e) =>
                            onChangeEntitlement(user.id, e.target.value)
                          }
                          className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1"
                          title="Yearly allowance for the selected year"
                        />
                      );
                    })()}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
  <input
    type="number"
    value={yearRemaining}
    readOnly
    title="Remaining for selected year"
    className={
      "text-sm border border-gray-300 rounded px-2 py-1 bg-gray-100 " +
      (yearRemaining < 0 ? "text-red-600 font-semibold" : "text-gray-900")
    }
  />
</td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <input
                      type="date"
                      value={user.start_date || "2025-01-01"}
                      onChange={(e) =>
                        handleInputChange(index, "start_date", e.target.value)
                      }
                      className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <select
                      value={user.department_id || ""}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "department_id",
                          e.target.value
                        )
                      }
                      className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="">Select Department</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap flex space-x-2">
                    <button
                      onClick={() => handleSave(user.id, user)}
                      className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setUserToDelete(user);
                        setIsDeleteModalOpen(true);
                      }}
                      className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Confirm Deletion</h2>
            <p>
              Do you really want to delete the user '{userToDelete?.firstName}{" "}
              {userToDelete?.lastName}'?
            </p>
            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(userToDelete.id)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Create New User</h2>

            <div className="mb-3">
              <input
                type="text"
                placeholder="First Name"
                value={newUser.firstName}
                onChange={(e) =>
                  handleNewUserInputChange("firstName", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>

            <div className="mb-3">
              <input
                type="text"
                placeholder="Last Name"
                value={newUser.lastName}
                onChange={(e) =>
                  handleNewUserInputChange("lastName", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>

            <div className="mb-3">
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) =>
                  handleNewUserInputChange("email", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>

            <div className="mb-3">
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) =>
                  handleNewUserInputChange("password", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>

            <div className="mb-3">
              <input
                type="number"
                placeholder="Vacation Hours (auto-calculated)"
                value={newUser.vacation_hours}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-gray-100"
              />
            </div>

            <div className="mb-3">
              <input
                type="number"
                placeholder="Total Vacation Hours"
                value={newUser.total_vacation_hours}
                onChange={(e) =>
                  handleNewUserInputChange(
                    "total_vacation_hours",
                    e.target.value
                  )
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>

            <div className="mb-3">
              <label className="block text-gray-700 mb-1">Start Date:</label>
              <input
                type="date"
                value={newUser.start_date}
                onChange={(e) =>
                  handleNewUserInputChange("start_date", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>

            <div className="mb-4">
              <select
                value={newUser.department_id}
                onChange={(e) =>
                  handleNewUserInputChange("department_id", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              >
                <option value="">Select Department</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
